import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { useCharacter } from '../Contexts/CharacterInfo.js';
import { TraitsDictionary } from '../Contexts/ConstantsCollection.js';
import theme from '../../../theme.js';
import TraitSelector from '../TraitSelector';
import AdditionalDamageRow from './AdditionalDamageRow';

const WeaponDialog = ({ isOpen, isSideWeapon, isEquipment, onClose }) => {
  const {
    character,
    addGearToStorage,
    updatePrimaryWeaponId,
    updateSecondaryWeaponId,
    updateEquipmentId,
  } = useCharacter();
  const [name, setName] = useState('');
  const [nameBorder, setNameBorder] = useState('2px solid black');
  const [description, setDescription] = useState('');
  const [isSpecial, setIsSpecial] = useState(false);
  const [isOversized, setIsOversized] = useState(false);
  const [selectedTrait, setSelectedTrait] = useState(
    TraitsDictionary.tough.rightSide
  );
  const [additionalDamageRows, setAdditionalDamageRows] = useState([]);
  const [totalUses, setTotalUses] = useState(0);
  const [isHealthItem, setIsHealthItem] = useState(false);
  const [isRepairItem, setIsRepairItem] = useState(false);

  const handleAddDamageRow = () => {
    if (additionalDamageRows.length >= 3) return;
    setAdditionalDamageRows(prev => [
      ...prev,
      { id: Date.now(), amount: 0, sides: 0, type: 'normal' },
    ]);
  };

  const handleUpdateDamage = (id, changes) => {
    setAdditionalDamageRows(prev =>
      prev.map(d => (d.id === id ? { ...d, ...changes } : d))
    );
  };

  const handleRemoveDamage = id => {
    setAdditionalDamageRows(prev => prev.filter(d => d.id !== id));
  };

  const clearFields = () => {
    setName('');
    setDescription('');
    setIsSpecial(false);
    setIsOversized(false);
    setSelectedTrait(TraitsDictionary.tough.rightSide);
    setAdditionalDamageRows([]);
    setIsHealthItem(false);
    setIsRepairItem(false);
    setTotalUses(0);
  };

  const handleClose = () => {
    setNameBorder('2px solid transparent');
    clearFields();
    onClose();
  };

  const handleSave = () => {
    if (name.trim() === '') {
      setNameBorder('2px solid red');
      return;
    }

    const gearId = crypto.randomUUID();

    if (isSideWeapon && character.secondaryWeaponId === '') {
      updateSecondaryWeaponId(gearId);
    } else if (isEquipment && character.equipmentId === '') {
      updateEquipmentId(gearId);
    } else if (character.primaryWeaponId === '') {
      updatePrimaryWeaponId(gearId);
    }

    addGearToStorage({
      id: gearId,
      name: name,
      trait: selectedTrait,
      isSpecial: isSpecial,
      isOversized: isOversized,
      isEquipment: isEquipment,
      isHealthItem: isHealthItem,
      isRepairItem: isRepairItem,
      totalUses: totalUses,
      remainingUses: totalUses,
      description: description,
      bonus: 0,
      additionalDamage: additionalDamageRows.map(d => [
        d.amount,
        d.sides,
        d.type,
      ]),
    });

    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEquipment ? 'Add Equipment' : 'Add Weapon'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch">
            <FormControl>
              <FormLabel>
                {isEquipment ? 'Equipment Name' : 'Weapon Name'}
              </FormLabel>
              <Input
                value={name}
                border={nameBorder}
                onChange={e => setName(e.target.value)}
                placeholder={
                  isEquipment ? 'e.g. Dowsing Rod' : 'e.g. Master Blaster 3000'
                }
              />
            </FormControl>
            <FormLabel>{isEquipment ? 'Use Trait' : 'Attack Trait'}</FormLabel>
            <TraitSelector
              SelectedTrait={selectedTrait}
              Traits={[
                TraitsDictionary.charm.leftSide,
                TraitsDictionary.charm.rightSide,
                TraitsDictionary.athletics.leftSide,
                TraitsDictionary.athletics.rightSide,
                TraitsDictionary.tough.leftSide,
                TraitsDictionary.tough.rightSide,
                TraitsDictionary.sharp.leftSide,
                TraitsDictionary.sharp.rightSide,
                TraitsDictionary.belief.leftSide,
                TraitsDictionary.belief.rightSide,
              ]}
              ignoreUsedTraits={true}
              setStyle={'default'}
              baseColor={theme.colors.buttonHover}
              onSelectionChanged={setSelectedTrait}
            />
            {!isSideWeapon && !isEquipment && (
              <HStack spacing={4} justifyContent={'center'}>
                <Checkbox
                  onChange={e => setIsSpecial(e.target.checked)}
                  fontWeight={'semibold'}
                >
                  Special Weapon
                </Checkbox>
                <Checkbox
                  onChange={e => setIsOversized(e.target.checked)}
                  fontWeight={'semibold'}
                >
                  Oversized Weapon
                </Checkbox>
              </HStack>
            )}
            {isEquipment && (
              <VStack spacing={4} mt={3} mb={3}>
                <HStack spacing={4} justifyContent={'center'}>
                  <Checkbox
                    isDisabled={isRepairItem}
                    onChange={e => setIsHealthItem(e.target.checked)}
                    fontWeight={'semibold'}
                  >
                    Health Item
                  </Checkbox>
                  <Checkbox
                    isDisabled={isHealthItem}
                    onChange={e => setIsRepairItem(e.target.checked)}
                    fontWeight={'semibold'}
                  >
                    Repair Item
                  </Checkbox>
                  <Text fontWeight={'semibold'} mr="-2">
                    Uses
                  </Text>
                  <NumberInput
                    min={0}
                    max={15}
                    width="75px"
                    value={totalUses === 0 ? '' : totalUses}
                    onChange={(valueString, valueNumber) => {
                      if (valueString === '') {
                        setTotalUses(0);
                      } else {
                        setTotalUses(valueNumber);
                      }
                    }}
                    onBlur={e => {
                      if (e.target.value === '') {
                        setTotalUses(0);
                      }
                    }}
                  >
                    <NumberInputField
                      border="2px solid lightgray"
                      height="40px"
                      placeholder="∞"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </HStack>
              </VStack>
            )}

            <FormControl>
              <FormLabel>
                {isHealthItem
                  ? 'Additional Healing'
                  : isRepairItem
                    ? 'Additional Repair'
                    : 'Additional Damage'}
              </FormLabel>
              <VStack spacing={4}>
                {additionalDamageRows.map(d => (
                  <AdditionalDamageRow
                    key={d.id}
                    id={d.id}
                    amount={d.amount}
                    sides={d.sides}
                    typeText={
                      isHealthItem
                        ? 'Healing Type'
                        : isRepairItem
                          ? 'Repair Type'
                          : 'Damage Type'
                    }
                    onAmountChange={(id, amt) =>
                      handleUpdateDamage(id, { amount: amt })
                    }
                    onSidesChange={(id, sides) =>
                      handleUpdateDamage(id, { sides: sides })
                    }
                    onTypeChange={(id, type) =>
                      handleUpdateDamage(id, { type: type })
                    }
                    onRemove={handleRemoveDamage}
                  />
                ))}
                <Button width="100%" onClick={handleAddDamageRow}>
                  <FaPlus size={20} />
                </Button>
              </VStack>
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minHeight={'100px'}
                resize={'vertical'}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={
                  isEquipment
                    ? 'e.g. It find the water good'
                    : 'e.g. It shoot real good'
                }
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={handleClose} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WeaponDialog;
